/**
 * HighwaySignType - Generic highway distance sign
 *
 * Displays upcoming game events/destinations sorted by distance:
 *   Pickup      87mi
 *   Mountains    142mi
 *
 * Uses the standard green highway sign model with white text.
 * Labels are left-aligned, distances are right-aligned.
 * Rows are automatically sorted by distance (nearest first).
 *
 * Signs spawn intelligently based on upcoming events rather than fixed intervals.
 */

import { biomeRegistry } from '../../registries/BiomeRegistry.js';
import { pickupRegistry } from '../../registries/PickupRegistry.js';

/**
 * Get the next wave target mile (accounting for uncollected waves)
 * @param {object} waves - gameState.state.waves
 * @param {number} currentMiles - Current player miles
 * @returns {number|null} - Target mile for next wave, or null if unavailable
 */
function getNextWaveTargetMile(waves, currentMiles) {
    if (!waves) return null;

    let nextWaveTarget = waves.nextWaveAtMile || waves.nextMilestone || 25;

    // If player is past current wave (uncollected), use first future wave
    if (currentMiles >= nextWaveTarget) {
        if (waves.futureWaves && waves.futureWaves.length > 0) {
            nextWaveTarget = waves.futureWaves[0].atMile;
        }
    }

    return nextWaveTarget;
}

/**
 * Extract pickup type ID from value that may be string or object
 * Handles legacy saved state where pickupType was stored as full object
 * @param {string|object} pickupType - Pickup type ID or object with id property
 * @returns {string} - Pickup type ID string
 */
function extractPickupTypeId(pickupType) {
    if (!pickupType) return 'wave';
    if (typeof pickupType === 'string') return pickupType;
    if (typeof pickupType === 'object' && pickupType.id) return pickupType.id;
    return 'wave';
}

/**
 * Get the pickup type for the next wave
 * @param {object} waves - gameState.state.waves
 * @param {number} currentMiles - Current player miles
 * @returns {string} - Pickup type ID (e.g., 'wave', 'salvage-crate')
 */
function getNextPickupType(waves, currentMiles) {
    if (!waves) return 'wave';

    const nextWaveTarget = waves.nextWaveAtMile || waves.nextMilestone || 25;

    // If player is past current wave (uncollected), use first future wave
    if (currentMiles >= nextWaveTarget) {
        if (waves.futureWaves && waves.futureWaves.length > 0) {
            return extractPickupTypeId(waves.futureWaves[0].pickupType);
        }
    }

    // Use current wave's pickup type
    return extractPickupTypeId(waves.nextPickupType);
}

/**
 * Get distance to next wave from a given mile position
 * @param {object} waves - gameState.state.waves
 * @param {number} milesAtSign - Miles at the sign's position
 * @returns {number|null} - Miles to next wave, or null if unavailable
 */
function getWaveDistance(waves, milesAtSign) {
    const targetMile = getNextWaveTargetMile(waves, milesAtSign);
    if (targetMile === null) return null;

    const distance = Math.ceil(targetMile - milesAtSign);
    return distance > 0 ? distance : null;
}

/**
 * Get distance to next biome transition
 * @param {object} gameState - Full game state
 * @param {number} milesAtSign - Miles at the sign's position
 * @returns {object|null} - { label, distance } or null if unavailable
 */
function getNextBiomeInfo(gameState, milesAtSign) {
    const nextBiome = biomeRegistry.getNextBiomeTransition(milesAtSign);
    if (nextBiome && nextBiome.atMile > milesAtSign) {
        const distance = Math.ceil(nextBiome.atMile - milesAtSign);
        if (distance > 0) {
            return {
                label: nextBiome.name,
                distance: distance
            };
        }
    }
    return null;
}

/**
 * Collect all upcoming notifications for the sign
 * @param {object} context - { gameState, milesAtSign, customData }
 * @returns {Array} - Array of { label, distance } objects
 */
function collectUpcomingDestinations(context) {
    const { gameState, milesAtSign, customData } = context;
    const destinations = [];

    // 1. Wave/pickup distance - use signLabel from pickup type registry
    const waves = gameState.state?.waves;
    const waveDistance = getWaveDistance(waves, milesAtSign);
    if (waveDistance !== null) {
        // Get pickup type for the upcoming pickup and use its signLabel
        const pickupTypeId = getNextPickupType(waves, milesAtSign);
        const pickupType = pickupRegistry.get(pickupTypeId);
        const label = pickupType?.signLabel || 'Pickup';

        destinations.push({
            label: label,
            distance: waveDistance
        });
    }

    // 2. Next biome transition (only show if within reasonable distance)
    const biomeInfo = getNextBiomeInfo(gameState, milesAtSign);
    if (biomeInfo && biomeInfo.distance > 0 && biomeInfo.distance <= HIGHWAY_SIGN_TYPE.maxEventDistance) {
        destinations.push(biomeInfo);
    }

    // 3. Custom destinations passed via customData
    if (customData?.destinations) {
        customData.destinations.forEach(dest => {
            const distance = Math.ceil(dest.atMile - milesAtSign);
            if (distance > 0) {
                destinations.push({
                    label: dest.label,
                    distance: distance
                });
            }
        });
    }

    // 4. Future: Add more notification sources here
    // - Prestige milestones
    // - Special events
    // - Rest areas
    // - Cities/landmarks

    return destinations;
}

/**
 * Collect all upcoming event mile markers for sign placement
 * @param {object} gameState - Full game state
 * @param {number} currentMiles - Current player miles
 * @returns {Array} - Array of mile markers where events occur
 */
function collectUpcomingEventMiles(gameState, currentMiles) {
    const eventMiles = [];

    // Wave events
    const waves = gameState.state?.waves;
    const waveTarget = getNextWaveTargetMile(waves, currentMiles);
    if (waveTarget !== null && waveTarget > currentMiles) {
        eventMiles.push(waveTarget);
    }

    // Also check future waves for additional sign placement
    if (waves?.futureWaves) {
        waves.futureWaves.forEach(fw => {
            if (fw.atMile > currentMiles) {
                eventMiles.push(fw.atMile);
            }
        });
    }

    // Add biome transitions (only if within displayable range)
    const nextBiome = biomeRegistry.getNextBiomeTransition(currentMiles);
    if (nextBiome && nextBiome.atMile > currentMiles) {
        const distance = nextBiome.atMile - currentMiles;
        if (distance <= HIGHWAY_SIGN_TYPE.maxEventDistance) {
            eventMiles.push(nextBiome.atMile);
        }
    }

    return eventMiles.sort((a, b) => a - b);
}

export const HIGHWAY_SIGN_TYPE = {
    id: 'highway',
    name: 'Highway Distance Sign',

    // Model configuration (uses default highway sign)
    modelPath: './assets/models/road_sign_v1.gltf',
    signMeshName: 'sign',

    // Visual styling
    backgroundColor: '#006600',  // Dark green (highway sign)
    textColor: '#FFFFFF',
    borderColor: '#FFFFFF',

    // Font configuration
    fontSizes: {
        single: 30,
        line1: 24,
        line2: 20
    },
    lineSpacing: 14,

    // Emissive glow for night visibility
    emissiveIntensity: 1.0,

    // Maximum rows to display on sign
    maxRows: 3,

    // Maximum distance ahead to show events (standard US highway signs show 500+ miles)
    maxEventDistance: 9999,

    /**
     * Check if a sign at the given position would have content to display
     * Called by SignManager to determine if a sign should spawn
     *
     * @param {object} gameState - Full game state
     * @param {number} milesAtSign - Mile marker where sign would be placed
     * @returns {boolean} - True if sign would have meaningful content
     */
    shouldSpawnSign: (gameState, milesAtSign) => {
        const eventMiles = collectUpcomingEventMiles(gameState, milesAtSign);

        // Check if any events are within displayable range from this sign position
        for (const eventMile of eventMiles) {
            const distance = eventMile - milesAtSign;
            if (distance > 0 && distance <= HIGHWAY_SIGN_TYPE.maxEventDistance) {
                return true;
            }
        }

        return false;
    },

    /**
     * Generate sign content based on game state and sign position
     * Returns rows format: { rows: [{ label, distance, fontSize? }] }
     * Rows are sorted by distance (nearest first)
     *
     * @param {object} context - { gameState, worldZ, milesAtSign, customData }
     * @returns {object} - { rows: [...] }
     */
    getContent: (context) => {
        // Collect all upcoming destinations
        const destinations = collectUpcomingDestinations(context);

        // Sort by distance (nearest first)
        destinations.sort((a, b) => a.distance - b.distance);

        // Limit to max rows and format
        const rows = destinations
            .slice(0, HIGHWAY_SIGN_TYPE.maxRows)
            .map(dest => ({
                label: dest.label,
                distance: dest.distance,
                fontSize: 24
            }));

        return { rows };
    }
};

// Export as WAVE_SIGN_TYPE for backward compatibility
export const WAVE_SIGN_TYPE = HIGHWAY_SIGN_TYPE;
